/**
 * OptiStack Internationalization Configuration
 * 
 * Supported locales and their metadata.
 * To add a new language: add an entry here + create the JSON files in locales/.
 */

export const locales = ['en', 'pt-BR', 'es', 'it', 'ru', 'de', 'tr'] as const;
export type Locale = (typeof locales)[number];

export const defaultLocale: Locale = 'en';

/**
 * The ONLY locale cookie the site honours. Written only by the Settings
 * language switcher, read only by src/middleware.ts — see the
 * "LOCALE PREFERENCE COOKIE" section there for why it is not next-intl's
 * NEXT_LOCALE and why the legacy name is expired on sight.
 */
export const LOCALE_PREFERENCE_COOKIE = 'optistack_locale';
export const LEGACY_LOCALE_COOKIE = 'NEXT_LOCALE';

/**
 * Locale metadata for UI display and configuration.
 * nativeName: How the language appears in its own script (for the language switcher)
 * flag: ISO country code for flag emoji/icon
 * dir: Text direction
 */
export const localeConfig: Record<Locale, {
  nativeName: string;
  englishName: string;
  flag: string;
  dir: 'ltr' | 'rtl';
  hreflang: string;
  // Countries that default to this locale (ISO 3166-1 alpha-2)
  defaultForCountries: string[];
}> = {
  en: {
    nativeName: 'English',
    englishName: 'English',
    flag: 'US',
    dir: 'ltr',
    hreflang: 'en',
    defaultForCountries: [
      'US', 'GB', 'AU', 'CA', 'NZ', 'IE', 'ZA', 'IN', 'SG', 'PH',
      'MY', 'NG', 'KE', 'GH', 'TZ', 'UG', 'JM', 'TT', 'BW', 'ZW',
    ],
  },
  'pt-BR': {
    nativeName: 'Português (Brasil)',
    englishName: 'Portuguese (Brazil)',
    flag: 'BR',
    dir: 'ltr',
    hreflang: 'pt-BR',
    defaultForCountries: ['BR'],
  },
  es: {
    nativeName: 'Español',
    englishName: 'Spanish',
    flag: 'ES',
    dir: 'ltr',
    hreflang: 'es',
    defaultForCountries: [
      'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU',
      'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY',
    ],
  },
  it: {
    nativeName: 'Italiano',
    englishName: 'Italian',
    flag: 'IT',
    dir: 'ltr',
    hreflang: 'it',
    defaultForCountries: ['IT', 'CH', 'SM', 'VA'],
  },
  de: {
    nativeName: 'Deutsch',
    englishName: 'German',
    flag: 'DE',
    dir: 'ltr',
    hreflang: 'de',
    // Deliberately empty. German exists as a locale so that
    // /de/kreismesser-fuer-laengsteilanlagen can be served and so that other
    // /de/* paths follow the same collapse to English as the other locales.
    // Mapping countries to it would change which landing page German speaking
    // visitors get at "/", which is not what this locale was added for.
    defaultForCountries: [],
  },
  ru: {
    nativeName: 'Русский',
    englishName: 'Russian',
    flag: 'RU',
    dir: 'ltr',
    hreflang: 'ru',
    defaultForCountries: [
      'RU', 'BY', 'KZ', 'KG', 'TJ', 'UZ', 'TM', 'MD',
    ],
  },
  tr: {
    nativeName: 'Türkçe',
    englishName: 'Turkish',
    flag: 'TR',
    dir: 'ltr',
    hreflang: 'tr',
    // Deliberately empty, exactly as for German. Turkish exists as a locale so
    // /tr/dilme-bicaklari and /tr/rulo-sac-dilme-hatti can be served and so
    // other /tr/* paths collapse to English like the other prefixes. Its
    // message catalogue mirrors English, so mapping TR here would route Turkish
    // browsers to /tr/landing and serve them English text under a Turkish
    // prefix, which is worse than the English landing they get today.
    defaultForCountries: [],
  },
};

/**
 * Map country code → default locale.
 * Built from localeConfig.defaultForCountries.
 * Countries not listed default to 'en'.
 */
export const countryToLocale: Record<string, Locale> = Object.entries(localeConfig)
  .flatMap(([locale, config]) =>
    config.defaultForCountries.map(country => [country, locale as Locale] as const)
  )
  .reduce((acc, [country, locale]) => {
    acc[country] = locale;
    return acc;
  }, {} as Record<string, Locale>);

/**
 * Get the best locale for a given country code.
 */
export function getLocaleForCountry(countryCode: string): Locale {
  return countryToLocale[countryCode.toUpperCase()] ?? defaultLocale;
}

/**
 * Translation namespaces.
 * Each namespace corresponds to a JSON file in each locale directory.
 */
export const namespaces = [
  'common',
  'landing',
  'app',
  'solver',
  'inventory',
  'pdf',
  'errors',
  'auth',
] as const;

export type Namespace = (typeof namespaces)[number];
